import { Component, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PricingDisplayPlan, PricingDisplayFeature } from '../../models';
import { TooltipDirective } from '../tooltip/tooltip.component';
import { TruncatePipe } from '../../pipes/truncate.pipe';

export type PlanCardSize = 'default' | 'compact' | 'mobile' | 'comparison-mobile';

/**
 * Reusable plan card component for pricing display.
 * Supports multiple size variants for different layouts.
 * 
 * Usage:
 * <plan-card 
 *   [plan]="plan" 
 *   [displayPrice]="getDisplayPrice(plan)"
 *   [currencySymbol]="getCurrencySymbol(plan)"
 *   size="default"
 *   (planSelected)="onPlanSelect($event)">
 * </plan-card>
 */
@Component({
  selector: 'plan-card',
  standalone: true,
  imports: [CommonModule, TooltipDirective, TruncatePipe],
  templateUrl: './plan-card.component.html'
})
export class PlanCardComponent {
  /** The plan data to display */
  readonly plan = input.required<PricingDisplayPlan>();
  
  /** Card size variant */
  readonly size = input<PlanCardSize>('default');
  
  /** Pre-computed display price (null for "Contact Sales") */
  readonly displayPrice = input<number | null>(null);
  
  /** Base price before discount (for strikethrough display) */
  readonly basePrice = input<number | null>(null);
  
  /** Price description text (e.g., "per seat", "for 3 seats") */
  readonly priceDescriptionText = input<string>('');
  
  /** Currency symbol to display */
  readonly currencySymbol = input<string>('$');
  
  /** Show the popular ring indicator */
  readonly showPopularRing = input<boolean>(true);
  
  /** Show highlighted features section (default size only) */
  readonly showHighlights = input<boolean>(false);
  
  /** Highlighted features to show */
  readonly highlightedFeatures = input<PricingDisplayFeature[]>([]);
  
  /** Show seats/users info */
  readonly showSeatsInfo = input<boolean>(false);
  
  /** Included seats count */
  readonly includedSeats = input<number>(0);
  
  /** Ranged value name (e.g., "Users", "Seats") */
  readonly rangedValueName = input<string>('Users');
  
  /** Additional seat price string */
  readonly additionalSeatPrice = input<string | null>(null);
  
  /** Extra CSS classes for description (e.g., hide-when-stuck) */
  readonly descriptionExtraClass = input<string>('');
  
  /** Replaces the "Choose Plan" button text when non-empty */
  readonly ctaLabel = input<string>('');

  /** Highlights the card as the current/pre-selected plan */
  readonly selected = input<boolean>(false);

  /** Plan selected event */
  readonly planSelected = output<PricingDisplayPlan>();
  
  // Computed formatted price
  readonly formattedPrice = computed(() => {
    const price = this.displayPrice();
    
    if (price === null || price === undefined) return 'Custom';
    if (price === 0) return 'FREE';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: price % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(price);
  });
  
  // Computed formatted base price (for strikethrough)
  readonly formattedBasePrice = computed(() => {
    const price = this.basePrice();
    if (price === null || price === undefined) return '';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: price % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(price);
  });
  
  // Show discount only when there's an actual discount (base > display)
  // This ensures discount only shows when appropriate (e.g., yearly billing)
  readonly showDiscount = computed(() => {
    const base = this.basePrice();
    const display = this.displayPrice();
    // Must have valid prices and base must be meaningfully higher (not just rounding)
    if (base === null || display === null) return false;
    // Show discount if base is at least 1% higher than display
    return base > display && (base - display) / base >= 0.001;
  });
  
  // Size-based classes
  readonly cardClasses = computed(() => {
    switch (this.size()) {
      case 'compact':
        return 'size-compact p-6 plan-card';
      case 'mobile':
        return 'size-mobile p-4';
      case 'comparison-mobile':
        return 'size-comparison-mobile p-2 gap-2';
      default:
        return 'size-default p-[26px]';
    }
  });
  
  readonly nameClasses = computed(() => {
    switch (this.size()) {
      case 'compact':
        return 'font-bold text-center text-base-700 mb-4 text-lg plan-name';
      case 'mobile':
        return 'font-bold text-center text-base-700 mb-4 text-base';
      case 'comparison-mobile':
        return 'font-semibold text-center text-base-700 mb-2 text-sm';
      default:
        return 'font-bold text-center text-base-700 mb-4 text-lg';
    }
  });
  
  readonly priceContainerClasses = computed(() => {
    switch (this.size()) {
      case 'compact':
        return 'text-center mb-4 plan-price';
      case 'comparison-mobile':
        return 'text-center mb-2';
      default:
        return 'text-center mb-4';
    }
  });
  
  readonly currencyClasses = computed(() => {
    const base = 'font-bold text-brand-purple-600';
    switch (this.size()) {
      case 'compact':
        return `${base} text-sm`;
      case 'mobile':
        return `${base} text-xs`;
      case 'comparison-mobile':
        return `${base} text-[10px]`;
      default:
        return `${base} text-base font-black leading-[20px]`;
    }
  });
  
  readonly priceClasses = computed(() => {
    const base = 'font-extrabold text-brand-purple-600 leading-none';
    switch (this.size()) {
      case 'compact':
        return `${base} text-2xl`;
      case 'mobile':
        return `${base} text-xl`;
      case 'comparison-mobile':
        return `${base} text-sm`;
      default:
        return `${base} text-[32px]`;
    }
  });
  
  readonly periodClasses = computed(() => {
    const base = 'font-medium text-base-400';
    switch (this.size()) {
      case 'compact':
        return `${base} text-sm`;
      case 'mobile':
        return `${base} text-xs`;
      case 'comparison-mobile':
        return `${base} text-[10px]`;
      default:
        return `${base} text-base leading-[20px]`;
    }
  });
  
  readonly priceDescriptionClasses = computed(() => {
    const base = 'text-brand-purple-600 font-normal';
    switch (this.size()) {
      case 'compact':
        return `${base} text-xs mt-1`;
      case 'mobile':
        return `${base} text-xs mt-1`;
      case 'comparison-mobile':
        return `${base} text-[10px] mt-1`;
      default:
        return `${base} text-xs mt-2`;
    }
  });
  
  readonly strikethroughPriceClasses = computed(() => {
    const base = 'text-base-300 line-through font-normal';
    switch (this.size()) {
      case 'compact':
        return `${base} text-xs`;
      case 'mobile':
        return `${base} text-xs`;
      case 'comparison-mobile':
        return `${base} text-[10px]`;
      default:
        return `${base} text-xs`;
    }
  });
  
  readonly seatsInfoClasses = computed(() => {
    const base = 'text-brand-purple-600 text-center';
    switch (this.size()) {
      case 'compact':
        return `${base} text-xs mt-2 leading-tight`;
      case 'comparison-mobile':
        return `${base} text-[10px] font-normal mt-1 leading-tight`;
      default:
        return `${base} text-xs mt-3 leading-tight`;
    }
  });
  
  readonly descriptionClasses = computed(() => {
    const base = 'text-center text-sm font-medium text-base-500 mb-4 leading-snug';
    const extra = this.descriptionExtraClass();
    switch (this.size()) {
      case 'compact':
        return `${base} flex-1 ${extra}`.trim();
      case 'mobile':
        return `${base} text-xs ${extra}`.trim();
      case 'comparison-mobile':
        return 'hidden'; // Hide description in comparison-mobile
      default:
        return `${base} ${extra}`.trim();
    }
  });
  
  readonly buttonClasses = computed(() => {
    const price = this.displayPrice();
    const colorClasses = price === null 
      ? 'bg-brand-purple-600 hover:bg-brand-purple-700 text-white' 
      : 'bg-brand-orange-500 hover:bg-brand-orange-600 text-white shadow-[0_16px_24px_-8px_rgba(0,0,0,0.16)]';
    
    switch (this.size()) {
      case 'compact':
        return `w-full h-8 px-4 rounded-md font-semibold text-sm transition-colors duration-200 ${colorClasses}`;
      case 'mobile':
        return `w-full h-8 px-3 rounded-md font-semibold text-xs transition-colors duration-200 ${colorClasses}`;
      case 'comparison-mobile':
        return `w-full py-1 px-1 rounded font-semibold text-xs leading-tight transition-colors duration-200 ${colorClasses}`;
      default:
        return `w-full h-10 px-4 rounded-md font-semibold text-base transition-colors duration-200 mb-4 ${colorClasses}`;
    }
  });
  
  /** Whether to show description (hidden in comparison-mobile) */
  readonly showDescription = computed(() => this.size() !== 'comparison-mobile');
  
  onPlanClick() {
    this.planSelected.emit(this.plan());
  }
}
